import { useState, useEffect } from "react";
import QuestionView from "../../../Admin/Question/View/QuestionView";

const QuestionPane = ({
  activeQuestions,
  currentQuestionIndex,
  solvedQuestions,
  examAnswers,
  saveExamAnswer,
  handlePrevQuestion,
  handleNextQuestion,
  questionOverlay,
  onCloseOverlay,
  onShowTrophyModal,
  handleExitSession,
  showFinalReportCard,
  isWaitingForEvaluation,
  finalEvaluationHtml,
  sessionStartTime,
  moreFontSize,
  lessFontSize,
  resetFontSize,
  FontSize,
  questionSeparated,
  title,
  t,
  isAnExam,
  onIsAnExamChange,
  onShowHint,
  showedHints,
  onResetAttempt,
  loading,
  onRegenerateQuestions,
  isWorkspaceExpanded,
  onToggleWorkspaceExpand,
  onGoToQuestion,
  originalBotMsg,
  originalBotMsgIndex,
  onSwitchToQuestionSet,
}) => {
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    setShowHint(false);
  }, [currentQuestionIndex]);

  if (!activeQuestions) {
    if (loading) {
      return (
        <div className="question-pane-empty-state text-center text-muted py-5">
          <i className="fal fa-spinner fa-spin empty-icon d-block mb-3 h2" style={{ color: "#92278f" }}></i>
          <p className="font-weight-bold text-dark">{t("Pages.Chatbot.RegeneratingQuestions") || "جاري إعادة التوليد..."}</p>
        </div>
      );
    }
    return (
      <div className="question-pane-empty-state text-center text-muted py-5">
        <i className="fal fa-clipboard-list-check empty-icon d-block mb-3 h2"></i>
        <p>{t("Pages.Chatbot.NoQuestions")}</p>
      </div>
    );
  }

  if (showFinalReportCard) {
    return (
      <div className="final-report-container h-100 d-flex flex-column">
        <div className="report-pane-header d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
          <h3 className="report-pane-title h6 font-weight-bold m-0 d-flex align-items-center text-right">
            <i className="fal fa-chart-line ml-2 text-primary"></i>
            <span>{t("Pages.Chatbot.FinalEvaluation")}</span>
          </h3>
          {!isWaitingForEvaluation && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary rounded-pill px-3 font-weight-bold d-flex align-items-center gap-1"
              onClick={onResetAttempt}
            >
              <i className="fal fa-redo ml-2"></i>
              <span>{t("Pages.Chatbot.SolveAgain")}</span>
            </button>
          )}
        </div>
        <div className="report-pane-body flex-grow-1 p-3 p-md-4 overflow-auto text-right">
          {isWaitingForEvaluation && !finalEvaluationHtml ? (
            <div className="text-center py-5">
              <div className="stream-waiting-spinner-container">
                <div className="stream-waiting-spinner-outer"></div>
                <div className="stream-waiting-spinner-inner"></div>
                <i className="fal fa-robot stream-waiting-icon"></i>
              </div>
              <h4
                style={{
                  color: "#0e2c53",
                  fontFamily: '"Cairo", sans-serif',
                  fontWeight: "bold",
                }}
              >
                {t("Pages.Chatbot.PreparingEvaluationReport")}
              </h4>
              <p className="text-muted">
                {t("Pages.Chatbot.PreparingEvaluationReportDesc")}
              </p>
            </div>
          ) : (
            <div className="evaluation-card card p-4 shadow-sm animate__animated animate__fadeIn">
              <div className="evaluation-header d-flex flex-column align-items-center text-center pb-3 border-bottom mb-3">
                {(() => {
                  const totalMarks = activeQuestions.reduce(
                    (acc, q) => acc + (q.Mark || 2),
                    0
                  );
                  const achievedMarks = Object.values(solvedQuestions).reduce(
                    (acc, sq) => acc + sq.userMark,
                    0
                  );
                  const percentage =
                    totalMarks > 0 ? Math.round((achievedMarks / totalMarks) * 100) : 0;

                  const timeElapsedSeconds = Math.round(
                    (Date.now() - sessionStartTime) / 1000
                  );
                  const minutes = Math.floor(timeElapsedSeconds / 60);
                  const seconds = timeElapsedSeconds % 60;

                  return (
                    <>
                      <div
                        className="evaluation-score-circle d-flex flex-column align-items-center justify-content-center bg-light border border-success rounded-circle mb-3"
                        style={{ width: "100px", height: "100px" }}
                      >
                        <span className="score-circle-value h3 font-weight-bold text-success m-0">
                          {achievedMarks.toFixed(1)}
                        </span>
                        <span className="score-circle-label text-muted small">
                          {t("Pages.Chatbot.From")} {totalMarks}
                        </span>
                      </div>
                      <h4
                        className="h5 font-weight-bold text-dark"
                        style={{ fontFamily: "Cairo, sans-serif" }}
                      >
                        {percentage >= 90
                          ? t("Pages.Chatbot.Excellent")
                          : percentage >= 75
                            ? t("Pages.Chatbot.VeryGood")
                            : percentage >= 50
                              ? t("Pages.Chatbot.Acceptable")
                              : t("Pages.Chatbot.NeedFocus")}
                      </h4>

                      <div className="evaluation-metrics d-flex align-items-center justify-content-center gap-3 flex-wrap mt-3">
                        <div className="metric-item p-2 px-3 bg-light rounded text-center">
                          <div className="metric-val font-weight-bold text-dark">
                            {percentage}%
                          </div>
                          <div className="metric-lbl small text-muted">
                            {t("Pages.Chatbot.Percentage")}
                          </div>
                        </div>
                        <div className="metric-item p-2 px-3 bg-light rounded text-center">
                          <div className="metric-val font-weight-bold text-dark">
                            {minutes}
                            {t("Pages.Chatbot.MinutesAbbr")}{" "}
                            {t("Pages.Chatbot.And")} {seconds}
                            {t("Pages.Chatbot.SecondsAbbr")}
                          </div>
                          <div className="metric-lbl small text-muted">
                            {t("Pages.Chatbot.TimeSpent")}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div
                className="evaluation-report-html text-right"
                dangerouslySetInnerHTML={{ __html: finalEvaluationHtml }}
              />

              {!isWaitingForEvaluation && (
                <div className="d-flex justify-content-center mt-4 border-top pt-3">
                  <button
                    type="button"
                    className="btn btn-primary rounded-pill px-4 py-2 font-weight-bold d-flex align-items-center gap-2"
                    onClick={onResetAttempt}
                    style={{ backgroundColor: "#92278f", borderColor: "#92278f" }}
                  >
                    <i className="fal fa-redo ml-2"></i>
                    <span>{t("Pages.Chatbot.SolveAgain")}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentQuestion = activeQuestions[currentQuestionIndex];
  const totalQuestions = activeQuestions.length;

  const achievedMarks = Object.values(solvedQuestions).reduce(
    (acc, sq) => acc + sq.userMark,
    0
  );
  const totalMarks = activeQuestions.reduce((acc, q) => acc + (q.Mark || 2), 0);
  const progressPercentage =
    totalQuestions > 0
      ? (Object.keys(solvedQuestions).length / totalQuestions) * 100
      : 0;

  return (
    <div className="questions-inner-pane h-100 d-flex flex-column animate__animated animate__fadeIn">
      <div className="questions-pane-header d-flex align-items-center justify-content-between p-3 border-bottom flex-wrap gap-2">
        <div className="header-info-group d-flex align-items-center flex-wrap gap-2 text-right">
          <h3 className="questions-pane-title h6 font-weight-bold m-0 d-flex align-items-center">
            <i className="fal fa-graduation-cap ml-2 text-primary"></i>
            <span>
              {title} ({currentQuestionIndex + 1} {t("Pages.Chatbot.From")}{" "}
              {totalQuestions})
            </span>
          </h3>
          <div className="lesson-zoom-toolbar mx-2">
            <button
              type="button"
              className="zoom-btn btn btn-sm btn-link text-muted p-1"
              onClick={moreFontSize}
              title={t("Pages.Missions.BookItem.tool.zoomIn")}
            >
              <i className="fal fa-plus"></i>
            </button>
            <button
              type="button"
              className="zoom-btn btn btn-sm btn-link text-muted p-1"
              onClick={resetFontSize}
              title={t("Pages.Chatbot.Reset")}
            >
              <i className="fal fa-history"></i>
            </button>
            <button
              type="button"
              className="zoom-btn btn btn-sm btn-link text-muted p-1"
              onClick={lessFontSize}
              title={t("Pages.Missions.BookItem.tool.zoomOut")}
            >
              <i className="fal fa-minus"></i>
            </button>
          </div>
          {onRegenerateQuestions && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill px-3 font-weight-bold d-flex align-items-center ml-2 regenerate-questions-btn"
              onClick={onRegenerateQuestions}
              disabled={loading}
              title={t("Pages.Chatbot.RegenerateQuestions")}
            >
              {loading ? (
                <>
                  <i className="fal fa-spinner fa-spin ml-2"></i>
                  <span>{t("Pages.Chatbot.RegeneratingQuestions")}</span>
                </>
              ) : (
                <>
                  <i className="fal fa-sync-alt ml-2"></i>
                  <span>{t("Pages.Chatbot.RegenerateQuestions")}</span>
                </>
              )}
            </button>
          )}
          {totalQuestions > 0 && (
            <div className="estimated-time-badge d-flex align-items-center px-2 py-1 rounded bg-light text-muted small ml-2">
              <i className="fal fa-clock ml-1"></i>
              <span>
                {Math.ceil(totalQuestions * 1.5)}{" "}
                {Math.ceil(totalQuestions * 1.5) > 1
                  ? t("Pages.Chatbot.Minutes")
                  : t("Pages.Chatbot.Minute")}{" "}
                {t("Pages.Chatbot.EstimatedSolvingTime")}
              </span>
            </div>
          )}
          <span
            className={`badge ${isAnExam ? "badge-danger" : "badge-info"} p-2 small font-weight-bold ml-2`}
            style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer", userSelect: "none" }}
            onClick={() => onIsAnExamChange && onIsAnExamChange(!isAnExam)}
          >
            <i className={`fal ${isAnExam ? "fa-file-signature" : "fa-laptop-code"}`}></i>
            <span>{isAnExam ? (t("Pages.Chatbot.ExamMode") || "وضع اختبار تقييمي") : (t("Pages.Chatbot.PracticeMode") || "وضع تمرين تفاعلي")}</span>
          </span>
          {originalBotMsg && originalBotMsg.questionsHistory && originalBotMsg.questionsHistory.length > 1 && (
            <div className="d-flex align-items-center ml-2 mr-1">
              <label className="text-muted small m-0 ml-1 font-weight-bold" style={{ whiteSpace: "nowrap" }}>
                {t("Pages.Chatbot.QuestionHistorySelect") || "نسخة الأسئلة:"}
              </label>
              <select
                className="form-control form-control-sm rounded-pill px-2 py-0"
                style={{
                  height: "28px",
                  fontSize: "12px",
                  fontFamily: "Cairo, sans-serif",
                  width: "auto",
                  minWidth: "120px",
                  borderColor: "rgba(14, 44, 83, 0.15)",
                  display: "inline-block"
                }}
                value={(() => {
                  const currentSerialized = JSON.stringify(
                    [
                      ...(originalBotMsg.dbQuestionIds || []).map(id => ({ Id: String(id), IsDbQuestion: true })),
                      ...(originalBotMsg.generatedQuestions || [])
                    ].map(q => q.Id || q.id)
                  );
                  const activeIdx = originalBotMsg.questionsHistory.findIndex(hist => {
                    const histSerialized = JSON.stringify((hist.questions || []).map(q => q.Id || q.id));
                    return histSerialized === currentSerialized;
                  });
                  return activeIdx !== -1 ? activeIdx : originalBotMsg.questionsHistory.length - 1;
                })()}
                onChange={(e) => onSwitchToQuestionSet && onSwitchToQuestionSet(parseInt(e.target.value))}
              >
                {originalBotMsg.questionsHistory.map((hist, idx) => {
                  const dateStr = new Date(hist.timestamp).toLocaleTimeString("ar-EG", { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  return (
                    <option key={idx} value={idx}>
                      {t("Pages.Chatbot.VersionNum", { num: idx + 1 }) || `نسخة #${idx + 1}`} ({hist.questions?.length || 0} {t("Pages.Chatbot.QuestionText") || "سؤال"}) - {dateStr}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          {!isAnExam && (
            <span className="questions-pane-score-badge badge badge-success p-2 small font-weight-bold ml-2">
              {t("Pages.Chatbot.AchievedMark")}: {achievedMarks.toFixed(1)}{" "}
              {t("Pages.Chatbot.From")} {totalMarks}
            </span>
          )}
        </div>
      </div>

      <div
        className="questions-progress-line-container bg-light w-100"
        style={{ height: "4px" }}
      >
        <div
          className="questions-progress-line bg-success h-100"
          style={{ width: `${progressPercentage}%`, transition: "width 0.4s ease" }}
        ></div>
      </div>

      <div className="questions-pane-body flex-grow-1 p-3 p-md-4 overflow-auto d-flex flex-column" style={{ position: "relative" }}>
        {loading && (
          <div className="loading-questions-overlay">
            <div className="overlay-spinner mb-3">
              <i className="fal fa-spinner fa-spin h2" style={{ color: "#92278f" }}></i>
            </div>
            <h5 className="font-weight-bold text-dark mb-1">{t("Pages.Chatbot.RegeneratingQuestions")}</h5>
            <p className="text-muted small mb-0">{t("PleaseWait") || "الرجاء الانتظار..."}</p>
          </div>
        )}
        {activeQuestions.length > 15 ? (
          <div className="questions-select-navigation-container d-flex align-items-center justify-content-center gap-2 mb-3 text-right" style={{ direction: "rtl" }}>
            <span className="font-weight-bold text-muted small ml-2">{t("Pages.Chatbot.GoToQuestion") || "الانتقال إلى السؤال:"}</span>
            <select
              value={currentQuestionIndex}
              onChange={(e) => onGoToQuestion && onGoToQuestion(parseInt(e.target.value))}
              className="form-control form-control-sm"
              style={{ width: "auto", minWidth: "150px", borderRadius: "12px", border: "1px solid rgba(14, 44, 83, 0.15)", fontFamily: "Cairo, sans-serif" }}
            >
              {activeQuestions.map((q, idx) => {
                const questionId = q.Id || q.id || `q_${idx}`;
                const solved = solvedQuestions[questionId];
                let prefix = "⚪";
                if (idx === currentQuestionIndex) {
                  prefix = "🔵";
                } else if (solved) {
                  if (isAnExam) {
                    prefix = "📝";
                  } else if (solved.userMark >= (q.Mark || 2)) {
                    prefix = "🟢";
                  } else {
                    prefix = "🔴";
                  }
                }
                return (
                  <option key={questionId} value={idx}>
                    {prefix} {t("Pages.Chatbot.QuestionNumber") || "سؤال"} {idx + 1}
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          <div className="questions-progress-tracker d-flex justify-content-center flex-wrap gap-2 mb-3">
            {activeQuestions.map((q, idx) => {
              const questionId = q.Id || q.id || `q_${idx}`;
              const solved = solvedQuestions[questionId];
              let dotClass = "progress-dot";
              if (idx === currentQuestionIndex) {
                dotClass += " active";
              } else if (solved) {
                if (isAnExam) {
                  dotClass += " solved-exam";
                } else if (solved.userMark >= (q.Mark || 2)) {
                  dotClass += " solved-correct";
                } else {
                  dotClass += " solved-incorrect";
                }
              }
              return (
                <div
                  key={questionId}
                  className={dotClass}
                  title={`${t("Pages.Chatbot.QuestionNumber") || "سؤال"} ${idx + 1}`}
                  onClick={() => onGoToQuestion && onGoToQuestion(idx)}
                  style={{ cursor: "pointer" }}
                >
                  {idx + 1}
                </div>
              );
            })}
          </div>
        )}

        <div className="visual-theater-card card p-3 p-md-4 mb-3 border shadow-sm rounded-lg flex-grow-1">
          <div
            className="visual-theater-body d-flex flex-column h-100"
            style={{ position: "relative" }}
          >
            <div className="question-renderer-wrapper flex-grow-1 text-right">
              <QuestionView
                key={currentQuestion.Id || currentQuestion.id || "current_q"}
                question={currentQuestion}
                answers={currentQuestion.Answers}
                index={currentQuestionIndex + 1}
                indexType={1}
                examAnswers={Object.keys(examAnswers || {}).map((key) => ({
                  Id: isNaN(key) ? key : Number(key),
                  answers: examAnswers[key],
                }))}
                saveExamAnswer={saveExamAnswer}
                onEdit={false}
                solveButton={false}
                FontSize={FontSize || 18}
                questionSeparated={questionSeparated}
                isExamQuestion={isAnExam}
              />
            </div>

            {(() => {
              const hintText = currentQuestion.hint || currentQuestion.Hint;
              if (!hintText) return null;
              return (
                <div className="question-hint-container mt-3 text-right">
                  {!showHint ? (
                    <button
                      type="button"
                      className="btn btn-outline-info btn-sm rounded-pill font-weight-bold shadow-sm"
                      onClick={() => {
                        setShowHint(true);
                        if (onShowHint) {
                          onShowHint(currentQuestion.Id || currentQuestion.id);
                        }
                      }}
                    >
                      <i className="fal fa-lightbulb-on ml-1"></i>
                      {t("Pages.Chatbot.ShowHint") || "عرض التلميح"}
                    </button>
                  ) : (
                    <div
                      className="alert alert-info animate__animated animate__fadeIn border-info text-right p-3 rounded shadow-sm"
                      style={{ borderRight: "4px solid #17a2b8" }}
                    >
                      <h5 className="h6 font-weight-bold text-info mb-2">
                        <i className="fal fa-lightbulb-on ml-2"></i>
                        {t("Pages.Chatbot.HintTitle") || "التلميح المساعد"}
                      </h5>
                      <p
                        className="m-0 text-dark"
                        style={{ fontSize: "14px", lineHeight: "1.5" }}
                      >
                        {hintText}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {!isAnExam && questionOverlay && questionOverlay.show && (
              <div
                className={`question-overlay-pane d-flex align-items-center justify-content-center animate__animated animate__fadeIn`}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255, 255, 255, 0.96)",
                  zIndex: 100,
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <div
                  className="overlay-content text-center animate__animated animate__zoomIn"
                  style={{ maxWidth: "500px", width: "100%" }}
                >
                  {questionOverlay.type === "success" && (
                    <>
                      <div className="overlay-icon-wrapper mb-3">
                        <i
                          className="fas fa-check-circle text-success animate__animated animate__bounceIn"
                          style={{ fontSize: "4.5rem" }}
                        ></i>
                      </div>
                      <h4
                        className="h5 font-weight-bold text-success mb-2"
                        style={{ fontFamily: "Cairo, sans-serif" }}
                      >
                        {t("Pages.Chatbot.CorrectAnswer")}
                      </h4>
                      <div
                        className="overlay-text text-muted mb-4 p-3 bg-light rounded text-right"
                        style={{
                          fontSize: "15px",
                          lineHeight: "1.6",
                          fontFamily: "Cairo, sans-serif",
                          borderRight: "4px solid #28a745",
                          unicodeBidi: "plaintext",
                        }}
                      >
                        {questionOverlay.text}
                      </div>
                      <button
                        className="btn btn-success btn-sm px-4 rounded-pill font-weight-bold shadow-sm"
                        onClick={() => onCloseOverlay(true)}
                      >
                        <span>{t("Pages.Chatbot.NextStage")}</span>{" "}
                        <i className="fal fa-arrow-left mr-1"></i>
                      </button>
                    </>
                  )}
                  {questionOverlay.type === "failure" && (
                    <>
                      <div className="overlay-icon-wrapper mb-3">
                        <i
                          className="fas fa-exclamation-circle text-danger animate__animated animate__wobble"
                          style={{ fontSize: "4.5rem" }}
                        ></i>
                      </div>
                      <h4
                        className="h5 font-weight-bold text-danger mb-2"
                        style={{ fontFamily: "Cairo, sans-serif" }}
                      >
                        {t("Pages.Chatbot.WrongAnswer")}
                      </h4>
                      <div
                        className="overlay-text text-muted mb-4 p-3 bg-light rounded text-right"
                        style={{
                          fontSize: "15px",
                          lineHeight: "1.6",
                          fontFamily: "Cairo, sans-serif",
                          borderRight: "4px solid #dc3545",
                          unicodeBidi: "plaintext",
                        }}
                      >
                        {questionOverlay.text}
                      </div>
                      <button
                        className="btn btn-danger btn-sm px-4 rounded-pill font-weight-bold shadow-sm"
                        onClick={() => onCloseOverlay(false)}
                      >
                        <span>{t("Pages.Chatbot.TryAgain")}</span>{" "}
                        <i className="fal fa-redo mr-1"></i>
                      </button>
                    </>
                  )}
                  {questionOverlay.type === "incomplete" && (
                    <>
                      <div className="overlay-icon-wrapper mb-3">
                        <i
                          className="fas fa-exclamation-triangle text-warning animate__animated animate__flash"
                          style={{ fontSize: "4.5rem" }}
                        ></i>
                      </div>
                      <h4
                        className="h5 font-weight-bold text-warning mb-2"
                        style={{ fontFamily: "Cairo, sans-serif" }}
                      >
                        {t("Pages.Chatbot.IncompleteTitle")}
                      </h4>
                      <div
                        className="overlay-text text-muted mb-4 p-3 bg-light rounded text-right"
                        style={{
                          fontSize: "15px",
                          lineHeight: "1.6",
                          fontFamily: "Cairo, sans-serif",
                          borderRight: "4px solid #ffc107",
                          unicodeBidi: "plaintext",
                        }}
                      >
                        {questionOverlay.text}
                      </div>
                      <button
                        className="btn btn-warning btn-sm px-4 rounded-pill font-weight-bold shadow-sm text-dark"
                        onClick={() => onCloseOverlay(false)}
                      >
                        <span>{t("Pages.Chatbot.TryAgain")}</span>{" "}
                        <i className="fal fa-arrow-left mr-1"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="question-navigation-row d-flex align-items-center justify-content-between border-top pt-3 mt-3">
          <button
            className="nav-btn prev-btn btn btn-outline-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
            disabled={
              currentQuestionIndex === 0 || (questionOverlay && questionOverlay.show)
            }
            onClick={handlePrevQuestion}
          >
            <i className="fal fa-arrow-right"></i>
            <span>{t("Previous")}</span>
          </button>

          {currentQuestionIndex < totalQuestions - 1 ? (
            <button
              className="nav-btn next-btn btn btn-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
              disabled={questionOverlay && questionOverlay.show}
              onClick={() => handleNextQuestion(onShowTrophyModal)}
            >
              <span>{t("Next")}</span>
              <i className="fal fa-arrow-left"></i>
            </button>
          ) : (
            <button
              className="nav-btn finish-btn btn btn-success btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1 shadow-sm"
              disabled={questionOverlay && questionOverlay.show}
              onClick={onShowTrophyModal}
            >
              <span>{t("Pages.Chatbot.FinishAndCalculate")}</span>
              <i className="fal fa-check-circle"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionPane;
